/**
 * Full media gallery — grid view, search, upload with real progress, rename, delete, infinite
 * scroll. Opens as a modal (no standalone page/route) from MediaPickerField, either to browse
 * everything or, when `lockKind` is set (an automation's template requires a specific header
 * type), constrained to just that type — the "only show matching media" requirement.
 */
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X, Search, Upload, Trash2, Pencil, Check, FileText, Image as ImageIcon, Loader2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  whatsappMediaApi, useListWaMediaQuery, useDeleteWaMediaMutation, useUpdateWaMediaMutation,
  waMediaContentUrl, uploadWaMediaWithProgress,
} from '../../api/whatsappMediaApi';
import { useInfiniteQuery } from '../../hooks/useInfiniteQuery';

const KIND_TABS = [
  { value: '', label: 'All' },
  { value: 'Image', label: 'Images' },
  { value: 'Video', label: 'Videos' },
  { value: 'Document', label: 'Documents' },
];

const ACCEPT = { Image: 'image/jpeg,image/png', Video: 'video/mp4', Document: 'application/pdf' };

const fmtSize = (bytes) => {
  const mb = (bytes ?? 0) / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round((bytes ?? 0) / 1024)} KB`;
};

const apiError = (e) => e?.message || e?.data?.error?.message || 'Something went wrong.';

export default function MediaGalleryModal({ lockKind, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState(lockKind || '');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const dispatch = useDispatch();
  const token = useSelector((s) => s.auth?.accessToken);
  const filter = useMemo(() => ({ search, kind: lockKind || kind || undefined }), [search, kind, lockKind]);
  const { items, total, hasMore, isFetching, sentinelRef } =
    useInfiniteQuery(useListWaMediaQuery, filter, { pageSize: 24 });

  const [deleteMedia] = useDeleteWaMediaMutation();
  const [updateMedia] = useUpdateWaMediaMutation();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    setProgress(0);
    try {
      const created = await uploadWaMediaWithProgress(file, token, setProgress);
      if (lockKind && created.kind !== lockKind) {
        toast.error(`That's a ${created.kind.toLowerCase()} file, but this template needs a ${lockKind.toLowerCase()} header.`);
      } else {
        toast.success(`${created.fileName} uploaded.`);
      }
      // Not an RTK Query mutation (plain XHR, for real progress), so the list's cache tag has to be
      // busted by hand — this is what makes the grid pick up the new file without a manual refresh.
      dispatch(whatsappMediaApi.util.invalidateTags([{ type: 'WaMedia', id: 'LIST' }]));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const startRename = (m) => { setRenamingId(m.id); setRenameValue(m.displayName); };
  const saveRename = async (id) => {
    const displayName = renameValue.trim();
    setRenamingId(null);
    if (!displayName) return;
    try {
      await updateMedia({ id, displayName }).unwrap();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.displayName}"? This cannot be undone.`)) return;
    try {
      await deleteMedia(m.id).unwrap();
      toast.success('Deleted.');
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 820, width: '92vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Media Gallery{lockKind ? ` — ${lockKind}s only` : ''}</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted)' }} />
              <input
                className="form-control" style={{ paddingLeft: 30 }}
                placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {!lockKind && (
              <div style={{ display: 'flex', gap: 4 }}>
                {KIND_TABS.map((t) => (
                  <button
                    key={t.value} type="button"
                    className={`btn btn-sm ${kind === t.value ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setKind(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            <label className="btn btn-outline btn-sm" style={{ cursor: uploading ? 'default' : 'pointer' }}>
              {uploading ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
              {uploading ? `Uploading… ${progress}%` : 'Upload new'}
              <input
                type="file" style={{ display: 'none' }} disabled={uploading}
                accept={lockKind ? ACCEPT[lockKind] : Object.values(ACCEPT).join(',')}
                onChange={handleUpload}
              />
            </label>
          </div>

          {uploading && (
            <div style={{ height: 4, borderRadius: 999, background: 'var(--gray-100)', overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'var(--blue-500)', transition: 'width 0.15s' }} />
            </div>
          )}

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10,
          }}>
            {items.map((m) => (
              <MediaCard
                key={m.id}
                m={m}
                renaming={renamingId === m.id}
                renameValue={renameValue}
                onRenameChange={setRenameValue}
                onStartRename={() => startRename(m)}
                onSaveRename={() => saveRename(m.id)}
                onDelete={() => remove(m)}
                onSelect={onSelect ? () => onSelect(m.id) : null}
              />
            ))}
          </div>

          {items.length === 0 && !isFetching && (
            <div className="empty-state" style={{ padding: 30 }}>
              <ImageIcon className="es-icon" size={32} />
              <h3>No media yet</h3>
              <p>{search ? 'Nothing matches that search.' : 'Upload a file to get started.'}</p>
            </div>
          )}

          {isFetching && <div className="skeleton" style={{ height: 40, marginTop: 10 }} />}
          <div ref={sentinelRef} />

          {items.length > 0 && (
            <div className="td-muted" style={{ fontSize: '0.78rem', textAlign: 'center', marginTop: 10 }}>
              {items.length.toLocaleString()} of {total.toLocaleString()}{hasMore ? ' — scroll for more' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MediaCard({ m, renaming, renameValue, onRenameChange, onStartRename, onSaveRename, onDelete, onSelect }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)', borderRadius: 'var(--r-md, 6px)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', cursor: onSelect ? 'pointer' : 'default',
      }}
      onClick={onSelect}
    >
      <div style={{ aspectRatio: '4 / 3', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {m.kind === 'Image' && (
          <img src={waMediaContentUrl(m.id)} alt={m.caption || m.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {m.kind === 'Video' && (
          // preload="metadata" is enough for the browser to show the first frame as a poster,
          // with no thumbnail-generation code needed on either side.
          <video src={waMediaContentUrl(m.id)} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {m.kind === 'Document' && <FileText size={32} style={{ color: 'var(--text-muted)' }} />}
      </div>

      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }} onClick={(e) => renaming && e.stopPropagation()}>
        {renaming ? (
          <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <input
              className="form-control" style={{ fontSize: '0.78rem', height: 26, padding: '2px 6px' }}
              autoFocus value={renameValue}
              onChange={(e) => onRenameChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSaveRename(); }}
            />
            <button type="button" className="btn-icon-sm" onClick={onSaveRename}><Check size={13} /></button>
          </div>
        ) : (
          <div style={{ fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.displayName}>
            {m.displayName}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="td-muted" style={{ fontSize: '0.7rem' }}>{fmtSize(m.sizeBytes)}</span>
          {!renaming && (
            <span style={{ display: 'flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
              <button type="button" className="btn-icon-sm" title="Rename" onClick={onStartRename}><Pencil size={12} /></button>
              <button type="button" className="btn-icon-sm" title="Delete" onClick={onDelete}><Trash2 size={12} /></button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
